<template>
  <div class="d-activity-feed">
    <DPostForm
      v-if="isSelf"
      :is-author="isAuthor"
      :user-avatar-url="userAvatarUrl"
      @post-created="postCreated"
    />

    <div
      v-if="isInitialized && isEmpty"
      class="panel text-center padding-top-8em padding-bottom-5em"
    >
      <img
        alt="No activity yet"
        class="space-bottom-3em"
        :src="emptyImageSrc"
      >
      <h2>
        {{ emptyTitle }}
      </h2>
      <p class="large">
        {{ emptyText }}
      </p>
    </div>

    <div
      v-if="isInitialized && !isEmpty"
      class="activities-container"
    >
      <div
        v-for="activity in activities"
        :key="getActivityKey(activity)"
        class="activity-entry-container"
      >
        <DPostForm
          v-if="activity.isEditing"
          :activity="activity"
          :is-author="isAuthor"
          :user-avatar-url="userAvatarUrl"
          @post-edited="postEdited"
        />

        <DActivityEntry
          :class="{ 'visible-until-sm': activity.isEditing }"
          :activity="activity"
          :link-to-activity="true"
          :user-avatar-url="userAvatarUrl"
          :user-name="userName"
          @activity-removed="activityRemoved"
          @post-created="postCreated"
        />
      </div>
    </div>

    <DuiLoadingIndicator
      v-if="isLoading"
      size="lg"
    />

    <div
      id="load-more-activities"
      class="load-more-activities"
    />
  </div>
</template>

<script>
import Vue from 'vue';

import activityFeedService from '@d-services/activity-feed-service/activity-feed-service';
import DActivityEntry from './d-activity-entry/d-activity-entry.vue';
import DPostForm from './d-post-form/d-post-form.vue';
import { getErrorMessage } from '@d-shared/utils/errors';
import { whenElementIsVisible } from '@dui/helpers/util-funcs';

import SATELLITE_IMG from '@d-assets/satellite.svg';

export default {
  components: {
    DActivityEntry,
    DPostForm,
  },
  props: {
    isAuthor: {
      type: Boolean,
      default: false,
    },
    isFeed: {
      type: Boolean,
      default: false,
    },
    isSelf: {
      type: Boolean,
      default: false,
    },
    userAvatarUrl: {
      type: String,
      default: null,
    },
    userName: {
      type: String,
      default: null,
    },
    userSlug: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    activities: [],
    disableLoadMore: null,
    emptyImageSrc: SATELLITE_IMG,
    isEmpty: false,
    isInitialized: false,
    isLoading: false,
    paginationInfo: {
      hasMoreResults: false,
      pageNum: 0,
    },
  }),
  computed: {
    emptyText() {
      if (!this.isEmpty) {
        return null;
      }

      if (this.isSelf) {
        return this.isFeed ?
          'Activity from people you follow will appear here.' :
          'As you browse Discovery, your recent activity will appear here.';
      }

      return 'Come back later to check updated activity.';
    },
    emptyTitle() {
      if (!this.isEmpty) {
        return null;
      }

      if (this.isSelf) {
        return this.isFeed ?
          'Your feed is empty' :
          'You have no recent activity!';
      }

      return 'No recent activity!';
    },
  },
  beforeDestroy() {
    if (this.disableLoadMore) {
      this.disableLoadMore();
    }
  },
  mounted() {
    this.loadFeed();
  },
  methods: {
    activityRemoved(removedActivity) {
      const activityIdx = this.activities.findIndex(a => a.slug === removedActivity.slug);
      if (activityIdx === -1) {
        return;
      }

      this.activities.splice(activityIdx, 1);
    },

    enableLoadMore() {
      if (this.disableLoadMore) {
        this.disableLoadMore();
      }

      const disableLoadMore = whenElementIsVisible('#load-more-activities', this.loadFeed.bind(this));
      this.$set(this, 'disableLoadMore', disableLoadMore);
    },

    getActivityKey(activity) {
      return activity.editedOn ?
        `${ activity.slug }-${ activity.editedOn }` :
        activity.slug;
    },

    async loadFeed() {
      this.$set(this, 'isLoading', true);

      try {
        this.paginationInfo.pageNum++;

        let feed;
        if (this.isFeed) {
          feed = await activityFeedService.getFeed(this.paginationInfo.pageNum);
        } else {
          feed = await activityFeedService.getUserActivities(this.userSlug, this.paginationInfo.pageNum);
        }

        this.$set(this, 'activities', this.activities.concat(feed.activities));
        this.$set(this.paginationInfo, 'hasMoreResults', !!feed.meta.next_page);

        if (this.paginationInfo.hasMoreResults) {
          this.enableLoadMore();
        }

        if (!this.isInitialized) {
          this.$set(this, 'isInitialized', true);
          if (!feed.activities.length) {
            this.$set(this, 'isEmpty', true);
          }
        }
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isLoading', false);
    },

    postCreated(newPost) {
      this.activities.unshift(newPost);

      if (!this.isEmpty) {
        return;
      }

      this.$set(this, 'isEmpty', false);
    },

    postEdited(editedPost) {
      const postIndex = this.activities.findIndex(a => a.slug === editedPost.slug);
      editedPost.editedOn = new Date().getTime();
      this.activities.splice(postIndex, 1, editedPost);
    },
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

.d-activity-feed {
  overflow: visible;

  @include screenMoreThan(sm) {
    margin-left: -$_2em;

    > .d-post-form {
      margin-bottom: $_2em;
    }
  }

  .activities-container {
    display: flex;
    flex-direction: column;

    @include screenLessThan(md) {
      background: getColor($grey);
      margin: 0 (-$_2em);
      padding: 0 $_2em;
    }

    .activity-entry-container {
      .d-activity-entry {
        margin: $_1em (-$_2em) 0;

        @include screenMoreThan(sm) {
          margin: 0 ;
        }

        &.white {
          margin-top: $_2em;
        }
      }

      &:first-child .d-activity-entry {
        margin-top: 0;
      }
    }
  }

  > .dui-loading-indicator {
    padding: $_4em 0;
    text-align: center;
    width: 100%;
  }
}
</style>
